/**
 * The comparable half of the feature table: what this market is compared ON,
 * with each product's value.
 *
 * Why it exists. Measured on a live board: 168 feature items with 168 distinct
 * titles, only 27 shared by more than one product — ~82% singleton rows, so
 * the matrix compared nothing. A phrase cannot be a row when the value is
 * inside the name ("5g creatine" and "3g Creapure creatine" are one attribute
 * at two values), and a compound ("5g Creatine + 800mg Electrolytes") is
 * several facts wearing one string. Here the row is "creatine dose" and the
 * cells read 5 g / 3 g / — across every product at once.
 *
 * It reads BOTH WAYS, from the table's own Flip. With sixty products and
 * thirteen attributes, products-down is the readable orientation: thirteen
 * columns fit on a screen, sixty do not — so that way round is a fixed
 * full-width grid, and this way round scrolls.
 *
 * It used to be written twice, a `<table>` per orientation, each a transpose
 * of the other. It is one column list now; `DataTable` does the turning.
 */
import { useMemo, useState } from 'react';
import { CARD } from './tableLayout';
import type { BoardroomMatrixProduct, BoardroomSpecRow } from '../../../types';
import DataTable from './DataTable';
import type { Column } from './tableColumns';
import type { SeenMarks } from '../useSeen';

/** How wide a column of this KIND wants to be, as a RATIO of the others.
 *
 *  Neither layout algorithm sizes these usefully on its own: `auto` gives the
 *  widest cell whatever it asks for, so one long ingredient list takes the
 *  screen; `fixed` with no widths splits the space equally, so a boolean
 *  holding "true" gets as much room as a paragraph. The sheet already records
 *  what each column IS, so the type does the sizing — which is the typed
 *  `kind` finally earning its keep. */
export const COL_WIDTH: Record<string, number> = {
  boolean: 5,
  quantity: 7,
  choice: 11,
  text: 18,
};
export const PRODUCT_COL = 15;

export function colWidth(kind: string): number {
  return COL_WIDTH[kind] ?? COL_WIDTH.text;
}

/** What one product has recorded for one attribute, or how it is unknown. */
function Value({ vals, product }: { vals: string[]; product: BoardroomMatrixProduct }) {
  if (vals.length > 0) {
    const text = vals.join(' / ');
    // The full value on hover: truncation must never be the only copy.
    return (
      <span className="text-gray-800 dark:text-gray-200" title={text}>
        {text}
      </span>
    );
  }
  // UNREAD IS NOT ABSENT, and a blank that means both is a table claiming
  // something it does not know. Nor is "read" the same as "checked": measured
  // on a live board, 56% of the empty cells belonged to products carrying
  // phrases nobody had fitted to a column — drawn as the same dash as "we
  // looked and it does not do that". Three states, because there are three.
  const unread = !product.read;
  const unfitted = product.unfitted ?? 0;
  const [glyph, why] = unread
    ? ['?', `${product.title} - nobody has read its pages, so this is unknown`]
    : unfitted > 0
      ? [
          '⋯',
          `${product.title} - read, but ${unfitted} of its recorded phrase${unfitted === 1 ? ' has' : 's have'} never been fitted to a column, so this is not yet a no`,
        ]
      : ['-', `${product.title} - read, and nothing it says fits this`];
  return (
    <span
      className={glyph === '-' ? 'text-gray-200 dark:text-gray-500' : 'text-gray-300 dark:text-gray-600'}
      title={why}
    >
      {glyph}
    </span>
  );
}

function attrLabel(r: BoardroomSpecRow, onOpenAttr?: (attr: string) => void, open = false) {
  const name = onOpenAttr ? (
    /* THE ONE THING THE CELLS CANNOT SAY. A cell reads "true"; it does not
       say that `API access`, `API delivery` and `low-latency REST API` were
       read as one column — which is the only question worth asking of a fresh
       induction. Turned sideways this becomes a COLUMN header, so the same
       button is the click target whichever way round the table is read. */
    <button
      type="button"
      onClick={() => onOpenAttr(r.attribute)}
      aria-expanded={open}
      className="hover:underline text-left"
      title={`The phrases folded into "${r.attribute}"`}
    >
      {open ? '▾ ' : '▸ '}
      {r.attribute}
    </button>
  ) : (
    r.attribute
  );
  return (
    <>
      {name}
      {r.unit && <span className="text-gray-400"> ({r.unit})</span>}
      {!r.on_sheet && (
        <span className="ml-1 text-amber-600 dark:text-amber-400" title="recorded, but not a column on the sheet yet">
          ·off-sheet
        </span>
      )}
    </>
  );
}

/** The value a cell shows, as a string — what the file gets. It says HOW it is
 *  unknown, because an export turning "nobody looked" and "looked, nothing
 *  there" both into blank asserts something the table is careful not to. */
export function specValue(vals: string[], product: BoardroomMatrixProduct): string {
  if (vals.length > 0) return vals.join(' / ');
  return product.read ? '' : 'not read';
}

export default function SpecTable({
  rows,
  products,
  onOpen,
  unmapped = 0,
  table = 'specs',
  working = false,
  title,
  toolbar,
  room,
  seen,
  featuresOf,
}: {
  rows: BoardroomSpecRow[];
  products: BoardroomMatrixProduct[];
  onOpen: (itemId: number) => void;
  /** THE REPORT'S tracker. A spec row is keyed by its ATTRIBUTE, not an item
   *  id, so the marks are on the PRODUCTS — which only the products-down view
   *  has rows for. It is the matrix's tracker either way: a tracker of its own
   *  here pruned every feature mark in the report. */
  seen?: SeenMarks;
  /** The features a product ships. The room's new items are FEATURES and this
   *  sheet has no feature rows — its rows are attributes — so a feature that
   *  lands lights the PRODUCT it belongs to, and reading that product reads
   *  them. Without it a matrix could say "13 new here" over a table with
   *  nothing lit in it at all. */
  featuresOf?: (productId: number) => readonly number[];
  unmapped?: number;
  /** Its own memory. The matrix draws one of these per market, and each keeps
   *  its own column choice and which way round it reads. */
  table?: string;
  /** Work out the columns from every recorded phrase at once. Absent where
   *  nobody may spend. */
  /** Fit the phrases nobody has recorded against a column yet. */
  /** A pass is already going, or one has been asked for. */
  working?: boolean;
  /** Anything else for the strip — the report's own dig button. */
  /** The card's heading, on the same line as the controls. */
  title?: React.ReactNode;
  toolbar?: React.ReactNode;
  room?: string;
}) {
  /* NOTHING RECORDED IS ONE FACT, NOT A COLUMN OF THEM. Measured on a live
     board: six of one table's fourteen products had no fact on any row, so
     six whole columns read "—" top to bottom — true, and a lot of ink for
     "we know nothing about these yet".
     Folded rather than dropped, because which products they are IS the
     finding — and folded by filtering the PRODUCT list, so it works the same
     whichever way round the table is read: fewer columns upright, fewer rows
     flipped. */
  const [showBlank, setShowBlank] = useState(false);
  const [openAttr, setOpenAttr] = useState<string | null>(null);
  const blank = useMemo(
    () =>
      products.filter(p => !rows.some(r => (r.cells[String(p.id)] ?? []).length > 0)),
    [products, rows],
  );
  // All of them blank is a table that has not been read yet, not a table with
  // nothing worth showing — hiding every column would leave a lone heading.
  const foldable = blank.length > 0 && blank.length < products.length;
  const shownProducts = useMemo(
    () =>
      foldable && !showBlank
        ? products.filter(p => !blank.includes(p))
        : products,
    [products, blank, foldable, showBlank],
  );

  /* THE ATTRIBUTE IS THE ROW, and each product is a column of it. Flipped,
     that is products down and attributes across — the same cells, read the
     other way, from this one list. */
  const columns = useMemo<Column<BoardroomSpecRow>[]>(
    () => [
      {
        key: '__attribute',
        label: 'compared on',
        flipLabel: 'product',
        always: true,
        width: 20,
        // Flipped, each attribute IS a column, and the sheet already records
        // what kind of thing it holds — so the type does the sizing.
        rowWidth: r => colWidth(r.kind),
        value: r => r.attribute,
        cell: r => (
          <span title={r.meaning || undefined}>
            {attrLabel(r, a => setOpenAttr(v => (v === a ? null : a)), openAttr === r.attribute)}
            <span
              className="ml-1 text-gray-400 tabular-nums"
              title={`${r.filled} of ${r.field_size} products have a value recorded for this`}
            >
              {r.filled}/{r.field_size} known
            </span>
          </span>
        ),
      },
      ...shownProducts.map(p => ({
        key: `product-${p.id}`,
        label: p.title,
        itemId: p.id,
        // Flipped, this column becomes a row, and its width comes from the
        // attribute it lines up under instead.
        width: PRODUCT_COL,
        head: (() => {
          const ids = featuresOf?.(p.id) ?? [];
          return (
            <button
              type="button"
              onClick={() => onOpen(p.id)}
              ref={seen && ids.length ? seen.markGroup([p.id, ...ids], `${table}-product-${p.id}`) : undefined}
              className={`hover:underline text-left rounded px-0.5 ${seen && ids.length ? seen.clsAny([p.id, ...ids]) : ''}`}
              data-testid={`spec-product-${p.id}`}
            >
              {p.title}
            </button>
          );
        })(),
        value: (r: BoardroomSpecRow) => specValue(r.cells[String(p.id)] ?? [], p),
        cell: (r: BoardroomSpecRow) => <Value vals={r.cells[String(p.id)] ?? []} product={p} />,
      })),
    ],
    [shownProducts, onOpen, openAttr, seen, featuresOf, table],
  );

  if (rows.length === 0) {
    /* THE DEAD END WAS THE PROBLEM. This said the phrases were "waiting for
       the cartographer" and gave nobody a way to ask — so a room sat at 167
       phrases and 0 columns, and the matrix went on comparing nothing.

       THE WAY TO ASK IS NOT HERE ANY MORE. It is one room-wide action, and
       this renders once per group: on a board with five markets it drew five
       identical buttons, and on the specs tab a sixth from the report's own
       control strip. Naming the hole is this table's job; offering the fix
       once is the report's. */
    return (
      <div className="shrink-0 flex flex-wrap items-baseline gap-2 text-xs text-gray-500 dark:text-gray-400" data-testid="spec-empty">
        <span>
          Nobody has said what this market is compared <em>on</em> yet.
          {unmapped > 0 && ` ${unmapped} feature${unmapped === 1 ? '' : 's'} recorded as phrases could be a dozen columns.`}
        </span>
      </div>
    );
  }

  const openRow = openAttr ? rows.find(r => r.attribute === openAttr) : undefined;

  // SHRINK-0 IS LOAD-BEARING. Every sibling in the report's flex column has it
  // — the group tables, the control rows, the missing list — so a child
  // without it is the only one flexbox may squeeze, and a long "what the table
  // cannot say yet" list crushed this to a few scrollable pixels.
  return (
    <div
      className={CARD}
      data-testid="spec-table"
    >
      <DataTable
        table={table}
        file="spec-sheet"
        room={room}
        title={title}
        toolbar={toolbar}
        columns={columns}
        rows={rows}
        rowKey={r => `spec-${r.attribute}`}
        testId="spec"
        /* One column per product: sixty of them do not divide a screen, so
           this way round scrolls. Flipped it is thirteen attributes, which
           do — a fixed full-width grid needing no sideways scroll at all. */
        defaultMode="long"
        flippedMode="fit"
        /* THE MANY GO DOWN THE SIDE. A hundred and nineteen products across
           the top is twelve rows of nothing you can read; the same grid
           turned is twelve columns that fit on a screen. Only the FIRST
           reading — the flip is remembered per table after that. */
        defaultFlipped={shownProducts.length > rows.length}
        /* THE LEGEND AND THE PANEL BELONG TO THE TABLE, under its controls.
           Drawn above the table separately, they pushed the controls into the
           middle of the card where they belonged to neither. */
        beforeTable={
          <>
        {/* OUTSIDE THE SCROLLER. The legend belongs to the table, not to its
            contents: inside the scrolling box it slid away sideways with the
            rows and disappeared behind the frozen column. */}
        <div className="text-xs text-gray-400 mb-2">
          {unmapped > 0 && (
            <span className="text-amber-700 dark:text-amber-300 mr-2" data-testid="spec-unmapped">
              {unmapped} feature{unmapped === 1 ? '' : 's'} still recorded as a phrase, not yet a column ·
            </span>
          )}
          <span className="text-gray-500 dark:text-gray-300">value</span> recorded · ? nobody read its pages · ⋯ has phrases nobody has fitted yet · - read, and nothing it says fits
          {foldable && (
            <button
              type="button"
              onClick={() => setShowBlank(v => !v)}
              aria-expanded={showBlank}
              className="ml-2 text-gray-500 dark:text-gray-400 hover:underline"
              data-testid="spec-blank-toggle"
            >
              {showBlank
                ? `▾ hide the ${blank.length} with nothing recorded`
                : `▸ ${blank.length} product${blank.length === 1 ? '' : 's'} with nothing recorded yet`}
            </button>
          )}
        </div>
        {openRow && (
          /* A PANEL, NOT A DETAIL ROW. `DataTable` drops row detail when the
             table is flipped — and this one flips itself whenever there are
             more products than columns, which is most of them. Under the table
             it reads the same either way round. */
          <div
            className="mb-2 rounded border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30 px-3 py-2 text-xs"
            data-testid={`spec-phrases-${openRow.attribute}`}
          >
            <div className="text-gray-500 dark:text-gray-400 mb-1">
              What the room actually recorded, folded into{' '}
              <span className="text-gray-700 dark:text-gray-200">{openRow.attribute}</span>
              {openRow.meaning && <span className="text-gray-400"> - {openRow.meaning}</span>}
            </div>
            {Object.keys(openRow.phrases ?? {}).length === 0 ? (
              <span className="text-gray-400 italic">
                Nothing has been recorded under this column yet.
              </span>
            ) : (
              <ul className="flex flex-col gap-0.5">
                {products.flatMap(p =>
                  (openRow.phrases?.[String(p.id)] ?? []).map(phrase => (
                    <li key={`${p.id}-${phrase}`} className="flex items-baseline gap-1.5">
                      <span className="text-gray-800 dark:text-gray-200">{phrase}</span>
                      <button
                        type="button"
                        onClick={() => onOpen(p.id)}
                        className="text-[11px] text-gray-400 hover:underline"
                      >
                        {p.title}
                      </button>
                    </li>
                  )),
                )}
              </ul>
            )}
          </div>
        )}
          </>
        }
        /* Products are what the room creates and what changes; an attribute is
           a column of the sheet. So the marks ride the products, which have
           rows only once the table is flipped. */
        seen={seen}
      />
    </div>
  );
}
